<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '54rem' }"
    :breakpoints="{ '960px': '92vw', '640px': '96vw' }"
    class="rfs-dialog"
  >
    <template #header>
      <div class="rfs-header">
        <div class="rfs-header__icon">
          <i :class="`pi ${isPickerStep ? 'pi-calendar' : 'pi-sliders-h'}`"></i>
        </div>
        <div class="rfs-header__text">
          <h2 class="rfs-header__title">إعدادات نموذج التسجيل</h2>
          <p class="rfs-header__subtitle" v-if="!isPickerStep && academicYear">{{ academicYear.name }}</p>
        </div>
      </div>
    </template>

    <!-- ── Step 1: pick an academic year (only when opened without a fixed one) ── -->
    <template v-if="isPickerStep">
      <p class="rfs-desc">
        اختر السنة الدراسية لتعديل الحقول التي تظهر في نموذج الحجز العام الخاص بها.
      </p>

      <div class="rfs-picker">
        <label class="rfs-picker__label">السنة الدراسية</label>
        <Dropdown
          v-model="pickerYearId"
          :options="gradeStore.activeGrades"
          optionLabel="name"
          optionValue="id"
          placeholder="اختر السنة الدراسية"
          class="w-100"
          :filter="gradeStore.activeGrades.length > 6"
        />
        <p v-if="!gradeStore.activeGrades.length" class="rfs-picker__empty">
          لا توجد سنوات دراسية مفعّلة حالياً.
        </p>
      </div>
    </template>

    <!-- ── Step 2: field settings ── -->
    <template v-else>
      <p class="rfs-desc">
        تحكم في الحقول التي تظهر في نموذج الحجز العام لهذه السنة الدراسية، وحدد ما إذا كانت إلزامية على الطالب.
      </p>

      <div v-if="loading" class="rfs-state">
        <i class="pi pi-spin pi-spinner"></i>
        <span>جارِ تحميل الإعدادات...</span>
      </div>

      <div v-else-if="loadError" class="rfs-state rfs-state--error">
        <i class="pi pi-exclamation-triangle"></i>
        <p>{{ loadError }}</p>
        <button type="button" class="rfs-btn rfs-btn--secondary" @click="load(resolvedYearId)">
          <i class="pi pi-refresh"></i> إعادة المحاولة
        </button>
      </div>

      <div v-else-if="fields" class="rfs-grid">
        <div class="rfs-field-card" v-for="field in FIELD_DEFS" :key="field.key">
          <div class="rfs-field-card__header">
            <div class="rfs-field-card__icon"><i :class="`pi ${field.icon}`"></i></div>
            <div class="rfs-field-card__text">
              <div class="rfs-field-card__name">{{ field.label }}</div>
              <div class="rfs-field-card__desc">{{ field.description }}</div>
            </div>
          </div>

          <div class="rfs-field-card__options">
            <div class="rfs-option-row">
              <div class="rfs-option-row__label">
                <i class="pi pi-eye"></i>
                <span>مرئي</span>
              </div>
              <InputSwitch v-model="fields[field.key].visible" />
            </div>
            <div class="rfs-option-row">
              <div class="rfs-option-row__label">
                <i class="pi pi-star-fill"></i>
                <span>إلزامي</span>
              </div>
              <InputSwitch
                v-model="fields[field.key].required"
                :disabled="!fields[field.key].visible"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="rfs-footer">
        <button type="button" class="rfs-btn rfs-btn--secondary" @click="visible = false">
          إلغاء
        </button>

        <button
          v-if="isPickerStep"
          type="button"
          class="rfs-btn rfs-btn--primary"
          :disabled="!pickerYearId"
          @click="confirmPicker"
        >
          <i class="pi pi-arrow-left"></i>
          متابعة
        </button>
        <button
          v-else
          type="button"
          class="rfs-btn rfs-btn--primary"
          :disabled="saving || loading || !fields"
          @click="save"
        >
          <i class="pi pi-spin pi-spinner-dotted" v-if="saving"></i>
          <i class="pi pi-check" v-else></i>
          حفظ الإعدادات
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import { useGradeStore } from '@/stores/useGradeStore.js';
import { useRegistrationFormSettingsStore } from '@/stores/useRegistrationFormSettingsStore.js';

const FIELD_DEFS = [
  { key: 'address', label: 'العنوان', description: 'عنوان سكن الطالب', icon: 'pi-map-marker' },
  { key: 'certificate_image', label: 'صورة الشهادة', description: 'صورة شهادة الطالب المرفوعة عند الحجز', icon: 'pi-image' },
  { key: 'whatsapp_number', label: 'رقم الواتساب', description: 'رقم تواصل منفصل عن رقم ولي الأمر', icon: 'pi-whatsapp' },
  { key: 'student_actual_grade', label: 'درجة الطالب الفعلية', description: 'الدرجة التي حصل عليها الطالب', icon: 'pi-chart-line' },
  { key: 'maximum_grade', label: 'الدرجة العظمى', description: 'تُستخدم لحساب نسبة الطالب', icon: 'pi-chart-bar' },
  { key: 'student_type', label: 'نوع الطالب', description: 'مستجد أو قديم', icon: 'pi-user' },
];

export default {
  name: 'RegistrationFormSettingsModal',
  components: { Dialog, Dropdown, InputSwitch },
  props: {
    modelValue: { type: Boolean, default: false },
    // Optional: when omitted, the dialog opens with a year-picker step first.
    academicYearId: { type: [Number, String], default: null },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      FIELD_DEFS,
      pickerYearId: null,
      loading: false,
      saving: false,
      loadError: null,
      academicYear: null,
      fields: null,
      // Explicit step state: selecting a year in the dropdown must not by itself flip the
      // view — only confirming with "متابعة" (which triggers the actual fetch) should.
      step: 'picker',
    };
  },
  computed: {
    gradeStore() {
      return useGradeStore();
    },
    store() {
      return useRegistrationFormSettingsStore();
    },
    resolvedYearId() {
      return this.academicYearId ?? this.pickerYearId;
    },
    isPickerStep() {
      return this.step === 'picker';
    },
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
  watch: {
    modelValue(isOpen) {
      if (!isOpen) return;
      this.pickerYearId = null;
      this.fields = null;
      this.loadError = null;
      if (this.academicYearId) {
        this.step = 'settings';
        this.load(this.academicYearId);
      } else {
        this.step = 'picker';
      }
    },
  },
  methods: {
    confirmPicker() {
      if (!this.pickerYearId) return;
      this.step = 'settings';
      this.load(this.pickerYearId);
    },
    async load(id) {
      this.loading = true;
      this.loadError = null;
      this.fields = null;
      try {
        const settings = await this.store.fetchSettings(id);
        this.academicYear = this.store.academicYear;
        this.fields = settings;
      } catch (error) {
        console.error('RegistrationFormSettingsModal: failed to load settings', error);
        this.loadError = error?.response?.status === 404
          ? 'نقطة الإعدادات غير متاحة على الخادم (404). قد تحتاج الواجهة الخلفية إلى تحديث.'
          : 'تعذر تحميل الإعدادات، برجاء التحقق من الاتصال والمحاولة مرة أخرى.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const saved = await this.store.updateSettings(this.resolvedYearId, this.fields);
        this.$emit('saved', saved);
        Swal.fire({ icon: 'success', title: 'تم حفظ الإعدادات', timer: 1200, showConfirmButton: false });
        this.visible = false;
      } catch {
        Swal.fire({ icon: 'error', title: 'حدث خطأ أثناء الحفظ', timer: 2000, showConfirmButton: false });
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    if (!this.gradeStore.activeGrades.length) {
      await this.gradeStore.fetchActiveGrades();
    }
  },
};
</script>

<style scoped>
/* ── Header (rendered inside PrimeVue's #header slot) ── */
.rfs-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.rfs-header__icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  background: var(--primary-light);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
}

.rfs-header__title {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.25;
}

.rfs-header__subtitle {
  font-size: 0.8125rem;
  color: var(--primary-color);
  font-weight: 600;
  margin: 0.1875rem 0 0;
}

/* ── Intro description ── */
.rfs-desc {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 1.25rem;
}

/* ── Step 1: year picker ── */
.rfs-picker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.25rem 0 1rem;
}

.rfs-picker__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
}

.rfs-picker__empty {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0.25rem 0 0;
}

/* ── Loading / error states ── */
.rfs-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  padding: 2.5rem 1rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.rfs-state i {
  font-size: 1.75rem;
}

.rfs-state--error i {
  color: var(--color-danger);
}

.rfs-state--error p {
  margin: 0;
  color: var(--text-secondary);
  text-align: center;
  max-width: 26rem;
}

/* ── Field cards grid ── */
.rfs-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.125rem;
}

.rfs-field-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: box-shadow var(--ease-base), border-color var(--ease-base), transform var(--ease-base);
}

.rfs-field-card:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.rfs-field-card:focus-within {
  box-shadow: var(--shadow-md);
}

.rfs-field-card__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.125rem;
  border-bottom: 1px solid var(--surface-border);
}

.rfs-field-card__icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  background: var(--primary-light);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.rfs-field-card__text {
  min-width: 0;
}

.rfs-field-card__name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
}

.rfs-field-card__desc {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
  line-height: 1.4;
}

.rfs-field-card__options {
  padding: 0.25rem 1.125rem;
}

.rfs-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
}

.rfs-option-row + .rfs-option-row {
  border-top: 1px solid var(--surface-border);
}

.rfs-option-row__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.rfs-option-row__label i {
  font-size: 0.8125rem;
  color: var(--text-muted);
}

/* ── Footer ── */
.rfs-footer {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  width: 100%;
}

.rfs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.875rem;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 700;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--ease-base);
}

.rfs-btn--primary {
  background: var(--primary-color);
  color: #fff;
}

.rfs-btn--primary:hover:not(:disabled) {
  background: var(--primary-dark);
  box-shadow: var(--shadow-primary);
  transform: translateY(-1px);
}

.rfs-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.rfs-btn--secondary {
  background: transparent;
  border-color: var(--surface-border);
  color: var(--text-secondary);
}

.rfs-btn--secondary:hover {
  border-color: var(--primary-color);
  color: var(--text-primary);
}

/* ── Scrolling ── */
:deep(.p-dialog-content) {
  max-height: 68vh;
  overflow-y: auto;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: var(--surface-border) transparent;
}

:deep(.p-dialog-content)::-webkit-scrollbar {
  width: 8px;
}

:deep(.p-dialog-content)::-webkit-scrollbar-track {
  background: transparent;
}

:deep(.p-dialog-content)::-webkit-scrollbar-thumb {
  background-color: var(--surface-border);
  border-radius: var(--radius-full);
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .rfs-grid {
    grid-template-columns: 1fr;
  }

  .rfs-footer {
    flex-direction: column-reverse;
  }

  .rfs-btn {
    width: 100%;
  }

  :deep(.p-dialog-content) {
    max-height: 72vh;
  }
}
</style>
